import React, { useState, useEffect } from 'react';
import { RichTextEditor } from './RichTextEditor';
import { useTranslation } from 'react-i18next';
import type { ReservationWithSlotsResponse, CancelSlotsResponse } from '../types/reservation';

interface ReservationDetailModalProps {
  isOpen: boolean;
  reservationId: string | null;
  onClose: () => void;
  onCancelComplete: () => void;
  onFetchReservation: (id: string) => Promise<ReservationWithSlotsResponse>;
  onCancelSlots: (id: string, slotIds: string[]) => Promise<CancelSlotsResponse>;
  onTogglePayment: (id: string) => Promise<any>;
  onUpdateNotes?: (id: string, notes: string) => Promise<any>;
  onReschedule?: (reservation: ReservationWithSlotsResponse) => void;
}

export const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({
  isOpen,
  reservationId,
  onClose,
  onCancelComplete,
  onFetchReservation,
  onCancelSlots,
  onTogglePayment,
  onUpdateNotes,
  onReschedule,
}) => {
  const { t } = useTranslation();
  const [reservation, setReservation] = useState<ReservationWithSlotsResponse | null>(null);
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isTogglingPayment, setIsTogglingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelResult, setCancelResult] = useState<CancelSlotsResponse | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paidSuccessMessage, setPaidSuccessMessage] = useState<string | null>(null);

  // Notes editing state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSuccessMessage, setNotesSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && reservationId) {
      loadReservation();
    } else {
      // Reset state when modal closes
      setReservation(null);
      setSelectedSlotIds([]);
      setError(null);
      setCancelResult(null);
      setShowConfirmation(false);
      setPaidSuccessMessage(null);
      setIsEditingNotes(false);
      setEditedNotes('');
      setNotesSuccessMessage(null);
    }
  }, [isOpen, reservationId]);

  const loadReservation = async () => {
    if (!reservationId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await onFetchReservation(reservationId);
      setReservation(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('reservationModal.failedToLoad');
      setError(errorMessage);
      console.error('Failed to load reservation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSlotSelection = (slotId: string, slotStatus: string) => {
    if (slotStatus === t('reservationModal.cancelled')) return; // Can't select cancelled slots

    setSelectedSlotIds((prev) =>
      prev.includes(slotId) ? prev.filter((id) => id !== slotId) : [...prev, slotId]
    );
  };

  const handleCancelSlots = async () => {
    if (!reservationId || selectedSlotIds.length === 0) return;

    setIsCancelling(true);
    setError(null);

    try {
      const result = await onCancelSlots(reservationId, selectedSlotIds);
      setCancelResult(result);
      setSelectedSlotIds([]);
      setShowConfirmation(false);

      // Reload reservation to show updated state
      await loadReservation();

      // Notify parent to refresh
      setTimeout(() => {
        onCancelComplete();
      }, 2000);
    } catch (err) {
      let errorMessage = err instanceof Error ? err.message : t('reservationModal.failedToCancel');
      
      // Handle specific API error messages with proper translations
      if (errorMessage.includes('Cannot cancel same-day or past reservations')) {
        errorMessage = t('errors.cannotCancelSameDayReservations');
      } else if (errorMessage.includes('not allowed') || errorMessage.includes('nie jest dozwolone')) {
        errorMessage = t('errors.cancellationNotAllowed');
      } else if (!errorMessage.startsWith(t('errors.'))) {
        // If it's not a translated error, use general error
        errorMessage = t('errors.generalError');
      }
      
      setError(errorMessage);
      console.error('Failed to cancel slots:', err);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleTogglePayment = async () => {
    if (!reservationId) return;

    setIsTogglingPayment(true);
    setError(null);
    setPaidSuccessMessage(null);

    try {
      await onTogglePayment(reservationId);
      setPaidSuccessMessage(
        isPaid
          ? t('reservationModal.markedAsUnpaidSuccess')
          : t('reservationModal.markedAsPaidSuccess')
      );
      
      // Reload reservation to show updated state
      await loadReservation();

      // Notify parent to refresh
      setTimeout(() => {
        onCancelComplete();
      }, 2000);
    } catch (err) {
      let errorMessage = err instanceof Error ? err.message : t('reservationModal.failedToMarkAsPaid');
      setError(errorMessage);
      console.error('Failed to mark reservation as paid:', err);
    } finally {
      setIsTogglingPayment(false);
    }
  };

  const handleStartEditNotes = () => {
    setEditedNotes(reservation?.notes || '');
    setIsEditingNotes(true);
    setNotesSuccessMessage(null);
  };

  const handleCancelEditNotes = () => {
    setIsEditingNotes(false);
    setEditedNotes('');
  };

  const handleSaveNotes = async () => {
    if (!reservationId || !onUpdateNotes) return;

    setIsSavingNotes(true);
    setError(null);
    setNotesSuccessMessage(null);

    try {
      await onUpdateNotes(reservationId, editedNotes);
      setNotesSuccessMessage(t('reservationModal.notesUpdatedSuccess'));
      setIsEditingNotes(false);

      // Reload reservation to show updated state
      await loadReservation();

      // Notify parent to refresh
      setTimeout(() => {
        onCancelComplete();
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('reservationModal.failedToUpdateNotes');
      setError(errorMessage);
      console.error('Failed to update notes:', err);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const activeSlots = reservation?.slots.filter((s) => s.status === 'Active') || [];
  const cancelledSlots = reservation?.slots.filter((s) => s.status === 'Cancelled') || [];
  const selectedSlotsTotal = reservation?.slots
    .filter((s) => selectedSlotIds.includes(s.id))
    .reduce((sum, s) => sum + s.price, 0) || 0;

  const isPaid =
    (reservation?.paymentId && reservation?.paymentStatus === 'COMPLETED') ||
    !!reservation?.productPurchaseId;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-brand-primary-deep rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">{t('reservationModal.title')}</h2>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading && (
            <div className="text-center py-8">
              <div className="text-ink-secondary">{t('reservationModal.loadingMessage')}</div>
            </div>
          )}

          {error && !reservation && (
            <div className="bg-brand-warm/20 border border-brand-warm/40 rounded-lg p-4 mb-4">
              <p className="text-brand-warm">{error}</p>
            </div>
          )}

          {reservation && (
            <>
              {/* Customer Info */}
              <div className="mb-6 p-4 bg-surface-card rounded-lg">
                <h3 className="text-lg font-semibold text-ink-primary mb-3">{t('reservationModal.customerInfo')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  {reservation.customerName && (
                    <div>
                      <p className="text-ink-secondary text-sm">{t('reservationModal.customerName')}</p>
                      <p className="text-ink-primary font-semibold">{reservation.customerName}</p>
                    </div>
                  )}
                  {reservation.customerPhone && (
                    <div>
                      <p className="text-ink-secondary text-sm">{t('reservationModal.customerPhone')}</p>
                      <p className="text-ink-primary">{reservation.customerPhone}</p>
                    </div>
                  )}
                  {reservation.customerEmail && (
                    <div className="col-span-2">
                      <p className="text-ink-secondary text-sm">{t('reservationModal.customerEmail')}</p>
                      <p className="text-ink-primary">{reservation.customerEmail}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reservation Info */}
              <div className="mb-6 p-4 bg-surface-card rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-ink-secondary text-sm">{t('reservationModal.reservationId')}</p>
                    <p className="text-ink-primary font-mono text-sm">{reservation.id}</p>
                  </div>
                  <div>
                    <p className="text-ink-secondary text-sm">{t('reservationModal.status')}</p>
                    <p className={`font-semibold ${
                      reservation.status === 'Active' ? 'text-brand-confirm-deep' :
                      reservation.status === 'Partial' ? 'text-brand-accent' :
                      reservation.status === 'Cancelled' ? 'text-brand-warm-deep' :
                      'text-ink-secondary'
                    }`}>
                      {reservation.status === 'Active' ? t('reservationModal.statusActive') :
                       reservation.status === 'Partial' ? t('reservationModal.statusPartial') :
                       reservation.status === 'Cancelled' ? t('reservationModal.statusCancelled') :
                       reservation.status}
                    </p>
                  </div>
                  <div>
                    <p className="text-ink-secondary text-sm">{t('reservationModal.paymentStatus')}</p>
                    <p className={`font-semibold ${
                      isPaid ? 'text-brand-confirm-deep' : 'text-brand-accent'
                    }`}>
                      {isPaid
                        ? t('reservationModal.paid')
                        : t('reservationModal.unpaid')}
                    </p>
                    {reservation.productPurchaseId && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 text-xs font-medium bg-brand-primary-light/20 text-brand-primary border border-brand-primary/30 rounded-full">
                        {t('reservationModal.paidWithProduct')}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-ink-secondary text-sm">{t('reservationModal.date')}</p>
                    <p className="text-ink-primary">{new Date(reservation.date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-ink-secondary text-sm">{t('reservationModal.totalPrice')}</p>
                    <p className="text-ink-primary font-semibold">{reservation.totalPrice} PLN</p>
                  </div>
                  {reservation.remainingPrice !== reservation.totalPrice && (
                    <div>
                      <p className="text-ink-secondary text-sm">{t('reservationModal.remainingPrice')}</p>
                      <p className="text-ink-primary font-semibold">{reservation.remainingPrice} PLN</p>
                    </div>
                  )}
                  {reservation.createdByName && (
                    <div>
                      <p className="text-ink-secondary text-sm">{t('reservationModal.createdBy')}</p>
                      <p className="text-ink-primary">{reservation.createdByName}</p>
                    </div>
                  )}
                  {reservation.createdAt && (
                    <div>
                      <p className="text-ink-secondary text-sm">{t('reservationModal.createdAt')}</p>
                      <p className="text-ink-primary">{new Date(reservation.createdAt).toLocaleString()}</p>
                    </div>
                  )}
                  </div>

                {/* Trainer Info */}
                {reservation.trainerProfileId && (
                  <div className="mt-4 pt-4 border-t border-border-strong">
                    <h4 className="text-sm font-semibold text-ink-primary mb-3">{t('reservationModal.trainerInfo')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-ink-secondary text-sm">{t('reservationModal.trainerName')}</p>
                        <p className="text-ink-primary font-semibold">{reservation.trainerDisplayName || reservation.trainerProfileId}</p>
                      </div>
                      {reservation.trainerPrice !== undefined && reservation.trainerPrice !== null && (
                        <div>
                          <p className="text-ink-secondary text-sm">{t('reservationModal.trainerPrice')}</p>
                          <p className="text-ink-primary font-semibold">{reservation.trainerPrice} PLN</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Group Booking Info */}
                {(reservation.numberOfUsers !== undefined && reservation.numberOfUsers > 1) && (
                  <div className="mt-4 pt-4 border-t border-border-strong">
                    <h4 className="text-sm font-semibold text-ink-primary mb-3">{t('reservationModal.groupBookingInfo')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-ink-secondary text-sm">{t('reservationModal.numberOfUsers')}</p>
                        <p className="text-ink-primary font-semibold text-lg">{reservation.numberOfUsers}</p>
                      </div>
                      <div>
                        <p className="text-ink-secondary text-sm">{t('reservationModal.paymentType')}</p>
                        <p className={`font-semibold ${reservation.payForAllUsers ? 'text-brand-primary' : 'text-brand-accent'}`}>
                          {reservation.payForAllUsers ? t('reservationModal.groupPayment') : t('reservationModal.individualPayment')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes Section - Always show with edit capability */}
                <div className="mt-4 pt-4 border-t border-border-strong">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-ink-secondary text-sm">{t('reservationModal.notes')}</p>
                    {onUpdateNotes && !isEditingNotes && (
                      <button
                        onClick={handleStartEditNotes}
                        className="text-xs text-brand-primary hover:text-brand-primary transition-colors"
                      >
                        {reservation.notes ? t('reservationModal.editNotes') : t('reservationModal.addNotes')}
                      </button>
                    )}
                  </div>
                  {isEditingNotes ? (
                    <div className="space-y-2">
                      <RichTextEditor
                        value={editedNotes}
                        onChange={setEditedNotes}
                        placeholder={t('reservationModal.notesPlaceholder')}
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={handleCancelEditNotes}
                          disabled={isSavingNotes}
                          className="px-3 py-1 text-sm bg-white border border-border-strong hover:bg-surface-card-hover text-ink-primary rounded transition-colors disabled:opacity-50"
                        >
                          {t('reservationModal.cancelEdit')}
                        </button>
                        <button
                          onClick={handleSaveNotes}
                          disabled={isSavingNotes}
                          className="px-3 py-1 text-sm bg-brand-primary hover:bg-brand-primary-hover text-white rounded transition-colors disabled:opacity-50"
                        >
                          {isSavingNotes ? t('reservationModal.savingNotes') : t('reservationModal.saveNotes')}
                        </button>
                      </div>
                    </div>
                  ) : reservation.notes ? (
                    <div
                      className="text-ink-primary text-sm mt-1 tiptap-content"
                      dangerouslySetInnerHTML={{ __html: reservation.notes }}
                    />
                  ) : (
                    <span className="text-ink-muted italic text-sm">{t('reservationModal.noNotes')}</span>
                  )}
                </div>

                {reservation.cancelledAt && (
                  <div className="mt-4 pt-4 border-t border-border-strong">
                    <p className="text-ink-secondary text-sm">{t('reservationModal.cancelledBy')} {reservation.cancelledByName}</p>
                    <p className="text-ink-muted text-xs">{new Date(reservation.cancelledAt).toLocaleString()}</p>
                    {reservation.cancellationNotes && (
                      <p className="text-ink-secondary text-sm mt-2">{t('reservationModal.note')}: {reservation.cancellationNotes}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Success Message */}
              {cancelResult && (
                <div className="bg-brand-confirm/20 border border-brand-confirm/40 rounded-lg p-4 mb-4">
                  <p className="text-brand-confirm font-semibold mb-2">
                    {t('reservationModal.successfullyCancelled')} {cancelResult.cancelledSlots.length} {t('reservationModal.slotsSelected')}
                  </p>
                  {cancelResult.refundAmount > 0 && (
                    <div className="text-sm text-brand-confirm">
                      <p>{t('reservationModal.refundAmount')}: {cancelResult.refundAmount} PLN ({cancelResult.refundPercentage}%)</p>
                      {cancelResult.refundFee > 0 && (
                        <p>{t('reservationModal.refundFee')}: {cancelResult.refundFee} PLN</p>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-brand-confirm mt-2">
                    {t('reservationModal.newStatus')}: {cancelResult.newStatus}
                  </p>
                </div>
              )}

              {/* Success Message for Mark as Paid */}
              {paidSuccessMessage && (
                <div className="bg-brand-confirm/20 border border-brand-confirm/40 rounded-lg p-4 mb-4">
                  <p className="text-brand-confirm font-semibold">
                    {paidSuccessMessage}
                  </p>
                </div>
              )}

              {/* Success Message for Notes Update */}
              {notesSuccessMessage && (
                <div className="bg-brand-confirm/20 border border-brand-confirm/40 rounded-lg p-4 mb-4">
                  <p className="text-brand-confirm font-semibold">
                    {notesSuccessMessage}
                  </p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-brand-warm/20 border border-brand-warm/40 rounded-lg p-4 mb-4">
                  <p className="text-brand-warm">{error}</p>
                </div>
              )}

              {/* Active Slots */}
              {activeSlots.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">{t('reservationModal.activeSlots')}</h3>
                  <div className="space-y-2">
                    {activeSlots.map((slot) => (
                      <div
                        key={slot.id}
                        onClick={() => toggleSlotSelection(slot.id, slot.status)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedSlotIds.includes(slot.id)
                            ? 'bg-brand-primary/15 border-brand-primary'
                            : 'bg-surface-app-elevated border-border-strong hover:border-border-strong'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selectedSlotIds.includes(slot.id)
                                ? 'bg-brand-primary/50 border-brand-primary'
                                : 'border-border-strong'
                            }`}>
                              {selectedSlotIds.includes(slot.id) && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <p className="text-ink-primary font-semibold">{slot.timeSlot}</p>
                              {typeof slot.price === 'number' && !isNaN(slot.price) && slot.price > 0 && (
                                <p className="text-ink-secondary text-sm">{slot.price} PLN</p>
                              )}
                            </div>
                          </div>
                          <span className="text-brand-confirm-deep text-sm font-semibold">{t('reservationModal.active')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cancelled Slots */}
              {cancelledSlots.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">{t('reservationModal.cancelledSlots')}</h3>
                  <div className="space-y-2">
                    {cancelledSlots.map((slot) => (
                      <div
                        key={slot.id}
                        className="p-4 rounded-lg border-2 bg-surface-app-elevated border-border-strong opacity-60"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-ink-primary font-semibold line-through">{slot.timeSlot}</p>
                            {typeof slot.price === 'number' && !isNaN(slot.price) && slot.price > 0 && (
                              <p className="text-ink-secondary text-sm">{slot.price} PLN</p>
                            )}
                          </div>
                          <span className="text-brand-warm-deep text-sm font-semibold">{t('reservationModal.cancelled')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Confirmation Dialog */}
              {showConfirmation && (
                <div className="mb-4 p-4 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
                  <p className="text-brand-warm font-semibold mb-2">{t('reservationModal.confirmCancellation')}</p>
                  <p className="text-white/90 text-sm mb-4">
                    {t('reservationModal.confirmCancelMessage')} {selectedSlotIds.length} {t('reservationModal.slotsTotaling')} {selectedSlotsTotal} PLN?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelSlots}
                      disabled={isCancelling}
                      className="px-4 py-2 bg-brand-warm text-white rounded-lg hover:bg-brand-warm-deep disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCancelling ? t('reservationModal.cancelling') : t('reservationModal.confirmCancel')}
                    </button>
                    <button
                      onClick={() => setShowConfirmation(false)}
                      disabled={isCancelling}
                      className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 transition-colors"
                    >
                      {t('reservationModal.goBack')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {reservation && activeSlots.length > 0 && !showConfirmation && (
          <div className="p-6 border-t border-white/10 flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              {t('reservationModal.close')}
            </button>
            <div className="flex gap-2">
              {onReschedule && (
                <button
                  onClick={() => {
                    if (reservation) {
                      onReschedule(reservation);
                    }
                  }}
                  className="px-4 py-2 bg-brand-primary-light text-white rounded-lg hover:bg-brand-primary transition-colors"
                >
                  {t('reservationModal.changeDate')}
                </button>
              )}
              <button
                onClick={handleTogglePayment}
                disabled={isTogglingPayment}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isPaid
                    ? 'bg-brand-accent hover:bg-brand-accent-hover'
                    : 'bg-brand-confirm hover:bg-brand-confirm-deep'
                }`}
              >
                {isTogglingPayment
                  ? (isPaid ? t('reservationModal.markingAsUnpaid') : t('reservationModal.markingAsPaid'))
                  : (isPaid ? t('reservationModal.markAsUnpaid') : t('reservationModal.markAsPaid'))
                }
              </button>
              <button
                onClick={() => setShowConfirmation(true)}
                disabled={selectedSlotIds.length === 0}
                className="px-4 py-2 bg-brand-warm text-white rounded-lg hover:bg-brand-warm-deep disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {t('reservationModal.cancelSelectedSlots')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
